import CloseIcon from '@mui/icons-material/Close';
import { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import dabaduIcon from 'assets/images/trade-in/dabadu-icon.png';
import { LEAD_GEN_MODAL_CLOSE, LOCAL_STORAGE_KEYS } from 'constants/CommonConstants';
import useQueryParams from 'hooks/UseQueryParams';
import i18n from 'i18next';
import React, { useState } from 'react';
import TagManager from 'react-gtm-module';
import { FieldValues, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import DynamicStepperForm from 'sharedComponents/dynamicStepperForm';
import StepperHeader from 'sharedComponents/dynamicStepperForm/StepperHeader';
import { StepConfigItem } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { DefaultModal } from 'sharedComponents/modal';
import { clearLeadCreation, postLeadCreation } from 'store/LeadCreationState';
import { RootState } from 'store/store';
import leadGenThankYouImg from '../../assets/images/lead-gen/thank-you.png';
import { MODULE_NAME } from './LeadGenConstant';
import { CustomWindow } from './LeadGenInterface';
import { getStepSpecificData } from './LeadGenUtils';
import { generateDynamicStepConfig, leadGenStepConfig2 } from './StepConfig';
import './lead-gen.scss';
import { clearProveData } from 'store/TradeInState';

const LeadGenStepperForm: React.FC = () => {
  // Hooks
  const { t } = useTranslation();
  const queryParams = useQueryParams();

  //Query Params
  const gtm_id = queryParams.get('gtm_id');
  const type = queryParams.get('type');
  const version = queryParams.get('version');

  let className;

  if (type === 'button' || type === 'banner') {
    className = 'leadgen-screens leadgen-screens--modal';
  } else if (type === 'embed') {
    className = 'leadgen-screens leadgen-screens--embedded';
  } else {
    className = 'leadgen-screens leadgen-screens--full-page';
  }

  // Fields to reset  when address option change
  const fieldsToReset = ['firstName', 'lastName', 'email', 'phone'];

  // Redux
  const dispatch: ThunkDispatch<RootState, AnyAction, AnyAction> = useDispatch();

  // Redux State

  const { proveVerificationData } = useSelector((state: RootState) => state.tradeInStore);

  const { leadCreationFormData } = useSelector((state: RootState) => state.leadCreationStore);

  // State
  const [initialStep, setInitialStep] = React.useState(0);
  const [isSuccessModal, setIsSuccessModal] = useState<boolean>(false);

  const { authenticateClientData } = useSelector((state: RootState) => state.auth) || {};
  const widgetData = authenticateClientData?.data?.widgetData || {};
  const showChat = widgetData?.showChat || 'FALSE';
  const gtmID = widgetData?.gtmID || '';

  // GTM initialize on component mount
  React.useEffect(() => {
    i18n.changeLanguage(authenticateClientData?.data?.widgetData?.language || 'en');

    if (gtmID) {
      // GTM initialize on component mount
      const tagManagerArgs = {
        gtmId: gtmID,
      };

      TagManager.initialize(tagManagerArgs);
    }

    // if (step && draftLeadId) {
    //   setInitialStep(parseInt(step, 10));

    //   // Need to call API to get data for this draftLeadId
    //   const leadData = {
    //     addressOption: 'lookup',
    //     firstName: 'sushant',
    //     lastName: 'test',
    //     email: 'testsush@gmail.com',
    //     phone: '11212121212',
    //     vehicleType: 'Truck',
    //     budget: 'UNDER $250 /MONTH',
    //   };

    //   // Save form data and current step to localStorage
    //   localStorage.setItem(`${MODULE_NAME}-currentStep`, step);
    //   localStorage.setItem(MODULE_NAME, JSON.stringify(leadData));
    //   localStorage.setItem(LOCAL_STORAGE_KEYS.draftLeadId, draftLeadId);
    // }
  }, [authenticateClientData, gtm_id]);

  React.useEffect(() => {
    console.log('proveVerificationData:', proveVerificationData);
    console.log('leadCreationFormData:', leadCreationFormData);
    const otpIndex = widgetData?.stepConfig.indexOf('VerifyOtpStep');

    if (
      (proveVerificationData?.success && proveVerificationData?.message === 'Prove Complete') ||
      (leadCreationFormData?.success && leadCreationFormData?.data?.Lead?.leadId && otpIndex < 0)
    ) {
      // Clear all data & Show Congratulation message
      clearLocalStorage();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proveVerificationData, leadCreationFormData]);

  // Reset fields when address option change
  const handleReset = (formRef) => {
    fieldsToReset.forEach((field) => formRef.setValue(field, ''));
  };

  const handleFinalSubmit: SubmitHandler<FieldValues> = async (formData, formRef) => {
    // Perform the final API call using formData

    const stepKey = widgetData.stepConfig[widgetData.stepConfig.length - 1];

    const payload = getStepSpecificData(stepKey, formData, widgetData, true);

    payload.leadDetails = {
      ...payload.leadDetails,
      staffUniqueId: widgetData?.staffUniqueId ?? '',
      associateDealerCode: widgetData?.associateDealerCode ?? '',
      organizationUniqueId: widgetData?.dealershipId || widgetData?.organizationUniqueId,
    };
    // return;

    dispatch(postLeadCreation({ data: payload, leadTypeID: widgetData?.leadTypeId })).then((res) => {
      console.log('Lead Creation Response :', res);
      if (res?.payload?.success) {
        // sendWindowDataLayer(formData);

        // Clear all data & Show Congratulation message
        handleReset(formRef);
        clearLocalStorage();
      }
    });
  };

  const sendWindowDataLayer = (formData: FieldValues) => {
    (window as CustomWindow).dataLayer = (window as CustomWindow).dataLayer || [];

    (window as CustomWindow)?.dataLayer?.push({
      event: 'Lead_Submission',
      customData: 'Lead Submitted Successfully',
      enhanced_conversion_data: {
        email: formData.email,
        phone_number: `+${formData.phone}`,
        first_name: formData.firstName,
        last_name: formData.lastName,
        street: formData.streetAddress,
        city: formData.city,
        region: formData.city,
        postal_code: formData.postalCode,
        country: formData.country,
      },
    });
  };

  const onCloseModal = () => {
    window.parent.postMessage({ type: LEAD_GEN_MODAL_CLOSE, data: { close: true }, res: { module: 'lead-gen' } }, '*');

    dispatch(clearLeadCreation());
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
  };

  const clearLocalStorage = () => {
    localStorage.setItem(`${MODULE_NAME}-currentStep`, '0');
    localStorage.removeItem(MODULE_NAME);
    localStorage.removeItem(LOCAL_STORAGE_KEYS.draftLeadId);

    setIsSuccessModal(true);
    setInitialStep(1);
    dispatch(clearLeadCreation());
    dispatch(clearProveData());
  };

  const stepHeaderByTypeAndVersion: {
    [type: string]: { [version: string]: React.ReactElement };
  } = {
    banner: {
      '1.0': <StepperHeader />,
    },
    button: {
      '1.0': <StepperHeader />,
    },
    embed: {
      '1.0': <StepperHeader />,
    },
  };

  // Step configurations mapped by type and version
  const stepConfigsByTypeAndVersion: {
    [type: string]: { [version: string]: StepConfigItem[] };
  } = {
    banner: {
      '1.0': generateDynamicStepConfig(widgetData, t),
      '1.1': leadGenStepConfig2(t),
    },
    button: {
      '1.0': generateDynamicStepConfig(widgetData, t),
      '1.1': leadGenStepConfig2(t),
    },
    embed: {
      '1.0': generateDynamicStepConfig(widgetData, t),
      '1.1': leadGenStepConfig2(t),
    },
  };

  const getStepHeader = (type: string | null, version: string | null) => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return null;
    }

    const typeConfig = stepHeaderByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return null;
    }

    const config = typeConfig[version];
    if (!config) {
      console.error(`No configuration found for type "${type}" and version "${version}"`);
      return null;
    }

    return config;
  };

  const getStepConfig = (type: string | null, version: string | null): StepConfigItem[] => {
    if (!type || !version) {
      console.error('Invalid type or version');
      return [];
    }

    const typeConfig = stepConfigsByTypeAndVersion[type];
    if (!typeConfig) {
      console.error('Unknown type:', type);
      return [];
    }

    const config = typeConfig[version];
    if (!config) {
      console.error(`No configuration found for type "${type}" and version "${version}"`);
      return [];
    }

    return config;
  };

  const stepConfig = getStepConfig(type, version);
  const stepHeaderByVersion = getStepHeader(type, version);
  return (
    <>
      <div className={className}>
        <div className='leadgen-screens__col leadgen-screens__col--right'>
          {window.self !== window.top && (
            <header className='leadgen-screens__col__header'>
              <img src={dabaduIcon} alt='Dabadu' className='img-fluid' />

              <span className='close' onClick={onCloseModal}>
                <CloseIcon />
              </span>
            </header>
          )}
          {initialStep !== null && (
            <DynamicStepperForm
              stepConfig={stepConfig}
              initialStep={initialStep}
              onFinish={handleFinalSubmit}
              moduleConfig={{
                stepperHeader: stepHeaderByVersion,
                name: MODULE_NAME,
              }}
            />
          )}
        </div>

        <DefaultModal
          open={isSuccessModal}
          handleClose={() => {
            setIsSuccessModal(false);
            onCloseModal();
          }}
          customClass='lead-gen-thankyou-modal'
          childern={
            <div className='modal-body'>
              <img src={leadGenThankYouImg} alt='Thank You' className='w-100' />
              <div>{t('leadGen.successMsg')}</div>
            </div>
          }
        />
      </div>
    </>
  );
};

export default LeadGenStepperForm;
